import { useMutation } from "@tanstack/react-query";
import {
  getEnumName,
  isAdminUser,
  OIDC,
  PermissionName,
  Permissions,
  service,
  User,
  useAccountQuery,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  Form,
  HelpIcon,
  notifyError,
  notifySuccess,
  Overlay,
  SelectFormField,
  UserIcon,
} from "@theprelude/ds";
import { useForm } from "react-hook-form";
import { SignInMethod } from "../sign-in-method";

const permsDescriptions: Partial<Record<PermissionName, string>> = {
  ADMIN: "This user will have full access to all capabilities within Prelude.",
  SERVICE:
    "This user will have limited access and will only be able to add new endpoints.",
  BUILD:
    "This user will have read access to the executive dashboard and read/write access to all Build actions in the CLI.",
  EXECUTIVE:
    "This user will have read access to the Prelude executive dashboard.",
};

type UpdateUserForm = {
  handle: string;
  permission: PermissionName;
  oidc?: OIDC;
};

const SelectedUserOverlay: React.FC<{
  onHide: () => void;
  user: User;
}> = ({ onHide, user }) => {
  const authConfig = useAuthConfig();
  const account = useAccountQuery();
  const isAdmin = isAdminUser(account.data) ?? false;

  const form = useForm<UpdateUserForm>({
    defaultValues: {
      permission: getEnumName(Permissions, user.permission),
      oidc: user.oidc,
    },
  });

  const { formState, register, watch } = form;

  const updateUser = useMutation({
    mutationFn: async ({ permission, oidc }: UpdateUserForm) => {
      await service(authConfig).iam.updateUser({
        handle: user.handle,
        permission,
        oidc: oidc ?? "",
      });
    },
  });

  const submit = (data: UpdateUserForm) => {
    updateUser.mutate(
      {
        ...data,
        handle: data.handle,
      },
      {
        onSuccess: () => {
          notifySuccess({
            message: "User updated successfully.",
            dismissible: false,
          });
          onHide();
        },
        onError: (error) => {
          notifyError({
            message: "Failed to update user",
            dismissible: false,
            error,
          });
        },
      },
    );
  };

  return (
    <Overlay
      position={"right"}
      title={"Update user information"}
      hideOverlay={onHide}
    >
      <div className="p-4">
        <Form
          {...form}
          onSubmitValid={submit}
          className="flex max-w-md flex-col gap-4"
        >
          <span className="flex items-center gap-1 border-stroke-line border-b pb-4 text-base">
            <UserIcon className="text-secondary-foreground" /> {user.handle}
          </span>

          <div className="flex items-center gap-2">
            Sign in method: <SignInMethod oidc={user.oidc} />
          </div>

          {isAdmin && (
            <SelectFormField
              {...register("permission")}
              label="Permissions"
              options={[
                { label: "Administrator", value: "ADMIN" },
                { label: "Build", value: "BUILD" },
                { label: "Executive", value: "EXECUTIVE" },
              ]}
            />
          )}

          <div className="flex items-center gap-2 text-sm">
            <HelpIcon className="flex-shrink-0 text-secondary-foreground" />
            <p>
              {permsDescriptions[watch("permission")]}{" "}
              <a
                href="https://support.preludesecurity.com/docs/permissions"
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2"
              >
                Learn more
              </a>
            </p>
          </div>

          <Button
            loading={updateUser.isPending}
            disabled={!formState.isDirty || updateUser.isPending}
            iconPosition="right"
            intent="primary"
            type="submit"
          >
            Update
          </Button>
        </Form>
      </div>
    </Overlay>
  );
};

export default SelectedUserOverlay;
